import React, { useEffect, useMemo, useState } from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import DataDisplay from '../../components/DataDisplay';
import { API } from '../../api/api_setup';
import { useAuth } from '../../hooks/useAuth';


const MlPodRequestMetricsPanel = ({ clusterName }) => {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('All')
  const [sortModel, setSortModel] = useState([{ field: 'timestamp', sort: 'desc' }])
  const [selectedDate, setSelectedDate] = useState(null)

  const columns = [
    { field: 'timestamp', headerName: 'Timestamp', width: 220 },
    { field: 'deployment', headerName: 'Deployment', width: 320 },
    { field: 'reserved', headerName: 'Reserved', type: 'number', width: 140 },
    { field: 'waiting', headerName: 'Waiting', type: 'number', width: 140 },
    { field: 'capacity', headerName: 'Capacity', type: 'number', width: 140 },
    { field: 'overcommit', headerName: 'Overcommit', type: 'number', width: 160 },
  ]

  useEffect(() => {
    setIsLoading(true)
    API.get(`/cluster_debug/ml_pod_request_metrics/${clusterName}?days=7`, {
      headers: { Authorization: `Bearer ${user.token}` }
    })
      .then((response) => {
        setRows(response?.data?.data || [])
      })
      .catch(() => { })
      .finally(() => setIsLoading(false))
  }, [clusterName, user.token])

  const deploymentOptions = useMemo(() => {
    const unique = new Set()
    rows.forEach((row) => {
      if (row?.deployment) {
        unique.add(row.deployment)
      }
    })
    return ['All', ...Array.from(unique).sort()]
  }, [rows])

  const filteredRows = useMemo(() => {
    let filtered = rows

    if (selectedDate) {
      const selectedDateStr = dayjs(selectedDate).format('YYYY-MM-DD')
      filtered = filtered.filter((row) => {
        if (!row.timestamp) return false
        const rowDateStr = String(row.timestamp).slice(0, 10)
        return rowDateStr === selectedDateStr
      })
    }

    const term = (searchTerm || '').trim().toLowerCase()
    if (term !== '' && term !== 'all') {
      const exactMatches = filtered.filter((row) => {
        const value = String(row?.deployment || '').toLowerCase()
        return value === term
      })
      if (exactMatches.length > 0) {
        filtered = exactMatches
      } else {
        filtered = filtered.filter((row) => {
          const value = String(row?.deployment || '').toLowerCase()
          return value.includes(term)
        })
      }
    }

    return filtered
  }, [rows, searchTerm, selectedDate])

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option || ''
  })

  return (
    <>
      <Grid container alignItems={'center'}>
        <Grid item sx={{ paddingLeft: '10px', paddingRight: '20px' }}>
          <Typography variant='h6' color={'#313638'}>{'ML Pod Request Metrics'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ padding: '10px' }}>
          <Autocomplete
            options={deploymentOptions}
            value={searchTerm}
            inputValue={searchTerm}
            onChange={(event, newValue) => setSearchTerm(newValue ?? '')}
            onInputChange={(event, newInputValue) => setSearchTerm(newInputValue ?? '')}
            freeSolo
            filterOptions={filterOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Deployment"
                size='small'
              />
            )}
            filterSelectedOptions
            disableClearable
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ padding: '10px' }}>
          <DatePicker
            label="Filter by Date"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid item lg={12} md={12} sx={{ paddingBottom: '16px' }}>
        <DataDisplay
          rows={filteredRows}
          columns={columns}
          loading={isLoading}
          sx={{ margin: '10px', marginTop: '0px' }}
          density='compact'
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
        />
      </Grid>
    </>
  )
}

export default MlPodRequestMetricsPanel;
